/**
 * One script: what it does, what version is current, and how to run it.
 *
 * The run form is the point of the page, so it comes first and everything else is
 * secondary.
 */

import { Fragment, useState } from "react";
import { Link, useParams } from "react-router-dom";

import type { ScriptDetail as ScriptDetailType, ScriptVersion } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { BuildHistory } from "@/components/BuildHistory";
import { BuildPanel } from "@/components/BuildPanel";
import { useProject } from "@/components/ProjectLayout";
import type { Manifest } from "@/components/manifest";
import { RunForm } from "@/components/RunForm";
import { ScriptNotifyTarget } from "@/components/ScriptNotifyTarget";
import { Badge, ErrorState, Loading, Notice, RelativeTime } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

function BuildBadge({ version }: { version: ScriptVersion }) {
  if (version.is_runnable) return <Badge tone="success">Runnable</Badge>;
  switch (version.build_status) {
    case "building":
    case "pending":
      return <Badge tone="accent" live>Building</Badge>;
    case "failed":
      return <Badge tone="danger">Build failed</Badge>;
    case "blocked":
      return <Badge tone="danger">Blocked by scan</Badge>;
    default:
      return <Badge>Not built</Badge>;
  }
}

function isBuildInFlight(version: ScriptVersion): boolean {
  return version.build_status === "building" || version.build_status === "pending";
}

function Versions({
  scriptRef,
  currentVersionId,
  onChanged,
}: {
  scriptRef: string;
  currentVersionId: string | null | undefined;
  onChanged: () => void;
}) {
  const { project, can } = useProject();
  const [busy, setBusy] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const [expanded, setExpanded] = useState<string>();

  const { data, loading, reload } = useAsync(
    () => endpoints.versions(project.slug, scriptRef),
    [project.slug, scriptRef],
  );

  const act = async (id: string, action: (id: string) => Promise<unknown>) => {
    setBusy(id);
    setProblem(undefined);
    try {
      await action(id);
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(undefined);
    }
  };

  if (loading) return <Loading rows={3} label="Loading versions" />;
  const versions = data ?? [];

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Version history</span>
        <span className="muted" style={{ fontSize: 12.5 }}>
          A run pins the version it used
        </span>
      </div>

      {problem && (
        <div style={{ padding: "12px 18px 0" }}>
          <Notice tone="danger">{problem}</Notice>
        </div>
      )}

      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Commit</th>
              <th>Message</th>
              <th>State</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {versions.map((version) => {
              const isCurrent = version.id === currentVersionId;
              const inFlight = isBuildInFlight(version);
              return (
                <Fragment key={version.id}>
                  <tr>
                    <td className="mono">
                      {version.short_sha}
                      {isCurrent && (
                        <>
                          {" "}
                          <Badge tone="accent">Current</Badge>
                        </>
                      )}
                    </td>
                    <td className="truncate muted" style={{ maxWidth: 320 }}>
                      {version.commit_message?.split("\n")[0] ?? "—"}
                    </td>
                    <td>
                      <BuildBadge version={version} />
                    </td>
                    <td className="muted">
                      <RelativeTime iso={version.created_at} />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {isCurrent && (
                        <span className="faint" style={{ fontSize: 12, marginRight: 8 }}>
                          Runs use this
                        </span>
                      )}
                      {!isCurrent && can("engineer") && version.is_runnable && (
                        <button
                          type="button"
                          className="button button--small"
                          disabled={busy === version.id}
                          onClick={() =>
                            act(version.id, (id) =>
                              endpoints.promoteVersion(project.slug, scriptRef, id),
                            )
                          }
                        >
                          Make current
                        </button>
                      )}
                      {can("engineer") && (
                        <button
                          type="button"
                          // Already-built versions get a ghost "Rebuild" — it is a
                          // deliberate re-run of the same commit, not the primary action
                          // a not-yet-built version needs.
                          className={
                            version.is_runnable
                              ? "button button--small button--ghost"
                              : "button button--small"
                          }
                          disabled={busy === version.id || inFlight}
                          title={
                            version.is_runnable
                              ? "Build this commit again, e.g. after the base image or a dependency changed"
                              : undefined
                          }
                          onClick={() =>
                            act(version.id, (id) =>
                              endpoints.buildVersion(project.slug, scriptRef, id),
                            )
                          }
                        >
                          {version.is_runnable ? "Rebuild" : "Build"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="button button--small button--ghost"
                        onClick={() =>
                          setExpanded(expanded === version.id ? undefined : version.id)
                        }
                      >
                        {expanded === version.id ? "Hide" : "Builds"}
                      </button>
                    </td>
                  </tr>
                  {expanded === version.id && (
                    <tr>
                      <td colSpan={5} style={{ background: "var(--surface-sunken)" }}>
                        <BuildPanel
                          scriptRef={scriptRef}
                          versionId={version.id}
                          onChanged={() => {
                            reload();
                            onChanged();
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScriptActions({
  script,
  onChanged,
}: {
  script: ScriptDetailType;
  onChanged: () => void;
}) {
  const { project } = useProject();
  const [editing, setEditing] = useState(false);
  const [branch, setBranch] = useState(script.branch);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  const fromRepo = Boolean(script.repo_full_name);

  const archive = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.archiveScript(project.slug, script.name);
      onChanged();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const saveBranch = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.updateScript(project.slug, script.name, { branch });
      setEditing(false);
      onChanged();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 6, alignItems: "flex-end" }}>
      <div className="row">
        {/* Only a repository script tracks a branch; an uploaded bundle has none. */}
        {fromRepo && !editing && (
          <button
            type="button"
            className="button button--small"
            onClick={() => setEditing(true)}
          >
            Change branch
          </button>
        )}
        {!script.archived_at && (
          <button
            type="button"
            className="button button--small button--danger"
            disabled={busy}
            onClick={archive}
          >
            Archive
          </button>
        )}
      </div>

      {editing && (
        <div className="row">
          <input
            className="input mono"
            style={{ width: 160 }}
            value={branch}
            aria-label="Branch"
            onChange={(event) => setBranch(event.target.value)}
          />
          <button
            type="button"
            className="button button--small button--primary"
            disabled={busy}
            onClick={saveBranch}
          >
            Save
          </button>
          <button
            type="button"
            className="button button--small button--ghost"
            onClick={() => {
              setEditing(false);
              setBranch(script.branch);
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {problem && <div className="field__error">{problem}</div>}
    </div>
  );
}

export function ScriptDetail() {
  const { scriptRef = "" } = useParams();
  const { project, can } = useProject();

  const { data, error, loading, reload } = useAsync(
    () => endpoints.script(project.slug, scriptRef),
    [project.slug, scriptRef],
  );

  if (loading) return <Loading rows={5} label="Loading script" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return null;

  // The API stores the manifest as an opaque document; the schema package is what
  // guarantees its shape, so reading it as a Manifest here is safe.
  const manifest = data.manifest as Manifest | null;

  return (
    <div className="stack" style={{ gap: 20 }}>
      <div className="breadcrumbs">
        <Link to={`/projects/${project.slug}`}>Scripts</Link>
        <span>/</span>
        <span>{data.name}</span>
      </div>

      <div className="row row--between">
        <div>
          <h2>{data.name}</h2>
          {data.description && <div className="muted">{data.description}</div>}
        </div>
        <div className="row">
          <Link
            to={`/projects/${project.slug}/runs?script_ref=${data.name}`}
            className="button button--small"
          >
            Run history
          </Link>
          {can("engineer") && <ScriptActions script={data} onChanged={reload} />}
        </div>
      </div>

      <div className="row muted" style={{ fontSize: 12.5, gap: 16 }}>
        <span className="mono">{data.entrypoint_path}</span>
        {data.repo_full_name ? (
          <>
            <span>{data.repo_full_name}</span>
            <span>branch {data.branch}</span>
          </>
        ) : (
          <Badge>Uploaded bundle</Badge>
        )}
        {data.current_version && <span className="mono">@{data.current_version.short_sha}</span>}
      </div>

      <ScriptNotifyTarget scriptId={data.id} />

      {!data.current_version ? (
        <Notice tone="warning" title="Not runnable yet">
          This script has no built version. Build one from the history below, and it
          becomes the current version automatically.
        </Notice>
      ) : !manifest ? (
        <Notice tone="warning" title="No manifest">
          The current version has no manifest, so there is no form to generate.
        </Notice>
      ) : can("pm") ? (
        <RunForm
          projectSlug={project.slug}
          scriptName={data.name}
          manifest={manifest}
          isPii={project.is_pii}
        />
      ) : (
        <Notice tone="info" title="You can view this script but not run it">
          Running needs the PM role or higher on this project.
        </Notice>
      )}

      {/* Every build attempt, across all versions, with its console right here — this
          is where a CodeBuild failure notification should send someone, rather than
          the per-version panel under Version history below. */}
      <BuildHistory scriptRef={scriptRef} />

      <Versions
        scriptRef={scriptRef}
        currentVersionId={data.current_version?.id}
        onChanged={reload}
      />
    </div>
  );
}
